import { useRef } from 'react'

import { cn } from '@lib/cn'

/**
 * Counts how many times the calling component has rendered.
 * Note: in React StrictMode (dev) renders are intentionally doubled, so absolute
 * numbers are 2x — only the relative before/after trend matters.
 */
const useRenderCount = () => {
  // Intentionally mutate + read the ref during render — this is a debug render
  // counter, so per-render increment is the whole point.
  /* eslint-disable react-hooks/refs */
  const count = useRef(0)
  count.current += 1

  return count.current
  /* eslint-enable react-hooks/refs */
}

type RenderCountProps = {
  /** `table`/`row` = absolute corner badge; `cell` = inline badge next to content. */
  variant?: 'table' | 'row' | 'cell'
}

/**
 * Tiny badge showing this subtree's render count. Each placed instance counts its
 * own renders, so a cell badge reflects that cell re-rendering and a row badge the row.
 * Callers decide whether to render it (gated by the `renderCounters` debug setting).
 */
export const RenderCount = ({ variant = 'cell' }: RenderCountProps) => {
  const count = useRenderCount()

  return (
    <span
      aria-hidden
      title={`renders: ${count}`}
      className={cn(
        'pointer-events-none absolute top-0 z-30 rounded bg-destructive px-1 text-[9px] font-semibold leading-none text-destructive-foreground tabular-nums',
        variant === 'cell' && 'right-0 opacity-70',
        variant === 'row' && 'left-0',
        variant === 'table' && 'right-1',
      )}
    >
      {count}
    </span>
  )
}
